import { EditorStarterAsset } from '../../assets/assets';
import { CaptionsItem } from '../../items/captions/captions-item-type';
import { EditorStarterItem } from '../../items/item-type';
import { EditorState, TrackType } from '../types';

const getAssetId = (item: EditorStarterItem): string | undefined =>
  'assetId' in item && typeof item.assetId === 'string'
    ? item.assetId
    : undefined;

const getLinkId = (item: EditorStarterItem): number | undefined => {
  const renderMeta = (
    item as {
      renderMeta?: { linkId?: number };
    }
  ).renderMeta;

  return typeof renderMeta?.linkId === 'number' ? renderMeta.linkId : undefined;
};

const getVideoSourceRange = (
  item: EditorStarterItem,
  fps: number,
): { start: number; end: number } | null => {
  if (item.type !== 'video') {
    return null;
  }

  const renderMeta = (
    item as {
      renderMeta?: {
        start?: number;
        end?: number;
        videoStart?: number;
        videoEnd?: number;
      };
    }
  ).renderMeta;
  const start =
    typeof item.videoStartFromInSeconds === 'number'
      ? Math.round(item.videoStartFromInSeconds * 1000)
      : typeof renderMeta?.start === 'number'
        ? renderMeta.start
        : renderMeta?.videoStart;

  if (typeof start !== 'number') {
    return null;
  }

  const playbackRate =
    typeof item.playbackRate === 'number' && item.playbackRate > 0
      ? item.playbackRate
      : 1;
  const durationMs = Math.round(
    (item.durationInFrames / fps) * 1000 * playbackRate,
  );
  const end = start + Math.max(0, durationMs);

  return { start, end };
};

const isSameSourceRange = (
  prevRange: { start: number; end: number } | null,
  nextRange: { start: number; end: number } | null,
): boolean =>
  prevRange !== null &&
  nextRange !== null &&
  prevRange.start === nextRange.start &&
  prevRange.end === nextRange.end;

const isMainLinkedTrack = (track: TrackType): boolean =>
  track.metadata?.isMainTrack === true &&
  track.metadata.trackType === 'main' &&
  (track.metadata.type === 'video' ||
    track.metadata.type === 'caption' ||
    track.metadata.type === 'audio');

const shouldPropagateLinkedShiftsFromTrack = (track: TrackType): boolean =>
  track.metadata?.isMainTrack === true &&
  track.metadata.trackType === 'main' &&
  track.metadata.type === 'video';

const shouldCollapseLeadingGapForTrack = (track: TrackType): boolean =>
  shouldPropagateLinkedShiftsFromTrack(track);

const shiftLinkedItemTiming = ({
  item,
  asset,
  shiftFrames,
  fps,
}: {
  item: EditorStarterItem;
  asset: EditorStarterAsset | undefined;
  shiftFrames: number;
  fps: number;
}): { item: EditorStarterItem; asset?: EditorStarterAsset } => {
  void fps;

  return { item: { ...item, from: item.from + shiftFrames }, asset };
};

const applyLinkedTrackShifts = ({
  state,
  sourceTrack,
  itemShiftFrames,
}: {
  state: EditorState;
  sourceTrack: TrackType;
  itemShiftFrames: Map<string, number>;
}): EditorState => {
  if (!shouldPropagateLinkedShiftsFromTrack(sourceTrack)) {
    return state;
  }

  const sourceShiftPlans: Array<{
    linkId: number;
    previousFrom: number;
    previousEnd: number;
    shiftFrames: number;
  }> = [];

  for (const [itemId, shiftFrames] of itemShiftFrames) {
    if (shiftFrames === 0) continue;

    const item = state.undoableState.items[itemId];
    if (!item) continue;

    const linkId = getLinkId(item);
    if (linkId == null) continue;

    const previousFrom = item.from - shiftFrames;
    sourceShiftPlans.push({
      linkId,
      previousFrom,
      previousEnd: previousFrom + item.durationInFrames,
      shiftFrames,
    });
  }

  if (sourceShiftPlans.length === 0) {
    return state;
  }

  const items = { ...state.undoableState.items };
  const assets = { ...state.undoableState.assets };
  let hasChanges = false;

  for (const track of state.undoableState.tracks) {
    if (track.id === sourceTrack.id) continue;
    if (!isMainLinkedTrack(track)) continue;

    for (const itemId of track.items) {
      if (itemShiftFrames.has(itemId)) continue;

      const item = items[itemId];
      if (!item) continue;

      const linkId = getLinkId(item);
      const itemEnd = item.from + item.durationInFrames;
      const shiftPlan =
        linkId == null
          ? undefined
          : sourceShiftPlans.find(
              (plan) =>
                plan.linkId === linkId &&
                item.from >= plan.previousFrom &&
                itemEnd <= plan.previousEnd,
            );
      const shiftFrames = shiftPlan?.shiftFrames;
      if (shiftFrames == null || shiftFrames === 0) continue;

      const assetId = getAssetId(item);
      const shifted = shiftLinkedItemTiming({
        item,
        asset: assetId == null ? undefined : assets[assetId],
        shiftFrames,
        fps: state.undoableState.fps,
      });

      items[itemId] = shifted.item;
      if (assetId != null && shifted.asset) {
        assets[assetId] = shifted.asset;
      }
      hasChanges = true;
    }
  }

  if (!hasChanges) {
    return state;
  }

  return {
    ...state,
    undoableState: {
      ...state.undoableState,
      items,
      assets,
    },
  };
};

const frameToMs = (frame: number, fps: number): number =>
  Math.round((frame / fps) * 1000);

const getTimelineEnd = (item: EditorStarterItem): number =>
  item.from + item.durationInFrames;

const syncCaptionsToChangedVideoRanges = (
  prevState: EditorState,
  state: EditorState,
): EditorState => {
  if (state.isLinkingEnabled === false) {
    return state;
  }

  const changedVideoRanges = new Map<
    number,
    {
      prevFrom: number;
      prevEnd: number;
      nextFrom: number;
      nextDurationInFrames: number;
      nextVideoStart: number;
      nextVideoEnd: number;
      sourceRangeChanged: boolean;
    }
  >();

  for (const track of state.undoableState.tracks) {
    if (!shouldPropagateLinkedShiftsFromTrack(track)) continue;

    for (const itemId of track.items) {
      const prevItem = prevState.undoableState.items[itemId];
      const nextItem = state.undoableState.items[itemId];
      if (!prevItem || !nextItem) continue;
      if (prevItem.type !== 'video' || nextItem.type !== 'video') continue;

      const linkId = getLinkId(nextItem);
      if (linkId == null) continue;
      if (
        prevItem.from === nextItem.from &&
        prevItem.durationInFrames === nextItem.durationInFrames
      ) {
        continue;
      }

      const prevSourceRange = getVideoSourceRange(
        prevItem,
        state.undoableState.fps,
      );
      const nextSourceRange = getVideoSourceRange(
        nextItem,
        state.undoableState.fps,
      );
      const sourceRangeChanged =
        prevSourceRange !== null || nextSourceRange !== null
          ? !isSameSourceRange(prevSourceRange, nextSourceRange)
          : prevItem.durationInFrames !== nextItem.durationInFrames;

      changedVideoRanges.set(linkId, {
        prevFrom: prevItem.from,
        prevEnd: getTimelineEnd(prevItem),
        nextFrom: nextItem.from,
        nextDurationInFrames: nextItem.durationInFrames,
        nextVideoStart:
          nextSourceRange?.start ??
          frameToMs(nextItem.from, state.undoableState.fps),
        nextVideoEnd:
          nextSourceRange?.end ??
          frameToMs(getTimelineEnd(nextItem), state.undoableState.fps),
        sourceRangeChanged,
      });
    }
  }

  if (changedVideoRanges.size === 0) {
    return state;
  }

  const items = { ...state.undoableState.items };
  const assets = { ...state.undoableState.assets };
  let hasChanges = false;

  for (const track of state.undoableState.tracks) {
    if (
      track.metadata?.isMainTrack !== true ||
      track.metadata.trackType !== 'main' ||
      track.metadata.type !== 'caption'
    ) {
      continue;
    }

    for (const itemId of track.items) {
      const prevCaption = prevState.undoableState.items[itemId];
      const nextCaption = items[itemId];
      if (
        !prevCaption ||
        !nextCaption ||
        prevCaption.type !== 'captions' ||
        nextCaption.type !== 'captions'
      ) {
        continue;
      }

      const linkId = getLinkId(nextCaption);
      const range = linkId == null ? undefined : changedVideoRanges.get(linkId);
      if (!range) continue;

      const wasAlignedToVideo =
        prevCaption.from === range.prevFrom &&
        getTimelineEnd(prevCaption) === range.prevEnd;
      if (!wasAlignedToVideo) {
        continue;
      }
      if (
        nextCaption.from !== range.prevFrom &&
        nextCaption.from !== range.nextFrom
      ) {
        continue;
      }

      const nextItem: CaptionsItem = {
        ...nextCaption,
        from: range.nextFrom,
        durationInFrames: range.nextDurationInFrames,
        ...(range.sourceRangeChanged
          ? {
              captionStartInSeconds: range.nextVideoStart / 1000,
              renderMeta: nextCaption.renderMeta
                ? {
                    ...nextCaption.renderMeta,
                    videoStart: range.nextVideoStart,
                    videoEnd: range.nextVideoEnd,
                  }
                : undefined,
            }
          : {}),
      };

      items[itemId] = nextItem;

      const asset = assets[nextCaption.assetId];
      if (range.sourceRangeChanged && asset?.type === 'caption') {
        assets[nextCaption.assetId] = {
          ...asset,
          videoStart: range.nextVideoStart,
          videoEnd: range.nextVideoEnd,
        };
      }

      hasChanges = true;
    }
  }

  if (!hasChanges) {
    return state;
  }

  return {
    ...state,
    undoableState: {
      ...state.undoableState,
      items,
      assets,
    },
  };
};

/**
 * Magnetic 트랙에서 간격을 생성하는 변경이 발생했는지 감지합니다.
 *
 * 간격 생성 조건:
 * 1. 아이템 삭제 (아이템 수 감소)
 * 2. 아이템 duration 축소 (동일 아이템의 durationInFrames 감소)
 *
 * ※ 드래그/이동은 별도 핸들러에서 처리하므로 여기서는 제외
 */
const hasGapCreatingChange = (
  prevState: EditorState,
  newState: EditorState,
  track: TrackType,
): boolean => {
  const prevItems = prevState.undoableState.items;
  const newItems = newState.undoableState.items;

  // 트랙의 아이템들을 확인
  for (const itemId of track.items) {
    const prevItem = prevItems[itemId];
    const newItem = newItems[itemId];

    // 아이템이 삭제된 경우는 트랙의 items 배열에서 이미 제거되므로 여기서 체크 안 됨
    // 대신 duration 축소 감지
    if (prevItem && newItem) {
      if (newItem.durationInFrames < prevItem.durationInFrames) {
        return true;
      }
    }
  }

  // 이전 상태의 트랙에서 아이템이 삭제되었는지 확인
  const prevTrack = prevState.undoableState.tracks.find(
    (t) => t.id === track.id,
  );
  if (prevTrack) {
    // 이전에 있던 아이템이 현재 트랙에서 사라졌는지 확인
    for (const prevItemId of prevTrack.items) {
      if (!track.items.includes(prevItemId)) {
        // 아이템이 트랙에서 제거됨 (삭제 또는 다른 트랙으로 이동)
        // 다른 트랙으로 이동한 경우는 제외 (아이템 자체가 존재하면)
        if (!newItems[prevItemId]) {
          return true;
        }
      }
    }
  }

  return false;
};

/**
 * 특정 트랙의 아이템들을 앞으로 당겨 간격을 제거합니다.
 * (collapse-track-gaps.ts의 collapseTrackGaps와 동일한 로직)
 */
const collapseGapsForTrack = (
  state: EditorState,
  track: TrackType,
): EditorState => {
  if (track.items.length === 0) {
    return state;
  }

  // 아이템들을 시작 프레임 순으로 정렬
  const sortedItems = track.items
    .map((id) => state.undoableState.items[id])
    .filter((item): item is EditorStarterItem => item !== undefined)
    .sort((a, b) => a.from - b.from);

  if (sortedItems.length === 0) {
    return state;
  }

  const nextInitialStartFrame = shouldCollapseLeadingGapForTrack(track)
    ? 0
    : (sortedItems[0]?.from ?? 0);
  let nextStartFrame = nextInitialStartFrame;
  const updatedItems: Record<string, EditorStarterItem> = {};
  const itemShiftFrames = new Map<string, number>();
  let hasChanges = false;

  for (const item of sortedItems) {
    if (item.from !== nextStartFrame) {
      const shiftFrames = nextStartFrame - item.from;
      updatedItems[item.id] = {
        ...item,
        from: nextStartFrame,
      };
      itemShiftFrames.set(item.id, shiftFrames);
      hasChanges = true;
    }
    nextStartFrame += item.durationInFrames;
  }

  if (!hasChanges) {
    return state;
  }

  const collapsedState = {
    ...state,
    undoableState: {
      ...state.undoableState,
      items: {
        ...state.undoableState.items,
        ...updatedItems,
      },
    },
  };

  return applyLinkedTrackShifts({
    state: collapsedState,
    sourceTrack: track,
    itemShiftFrames,
  });
};

/**
 * 특정 트랙에 간격이 있는지 확인합니다.
 */
const hasGapsInTrack = (state: EditorState, track: TrackType): boolean => {
  if (track.items.length === 0) {
    return false;
  }

  const sortedItems = track.items
    .map((id) => state.undoableState.items[id])
    .filter((item): item is EditorStarterItem => item !== undefined)
    .sort((a, b) => a.from - b.from);

  if (
    shouldCollapseLeadingGapForTrack(track) &&
    sortedItems.length > 0 &&
    (sortedItems[0]?.from ?? 0) > 0
  ) {
    return true;
  }

  if (sortedItems.length <= 1) {
    return false;
  }

  for (let i = 0; i < sortedItems.length - 1; i++) {
    const current = sortedItems[i];
    const next = sortedItems[i + 1];
    if (!current || !next) continue;

    const currentEnd = current.from + current.durationInFrames;
    if (next.from > currentEnd) {
      return true; // 간격 발견
    }
  }

  return false;
};

/**
 * 특정 트랙에 겹침이 있는지 확인합니다.
 */
const hasOverlapsInTrack = (state: EditorState, track: TrackType): boolean => {
  if (track.items.length <= 1) {
    return false;
  }

  const sortedItems = track.items
    .map((id) => state.undoableState.items[id])
    .filter((item): item is EditorStarterItem => item !== undefined)
    .sort((a, b) => a.from - b.from);

  for (let i = 0; i < sortedItems.length - 1; i++) {
    const current = sortedItems[i];
    const next = sortedItems[i + 1];
    if (!current || !next) continue;

    const currentEnd = current.from + current.durationInFrames;
    if (next.from < currentEnd) {
      return true; // 겹침 발견
    }
  }

  return false;
};

/**
 * Magnetic 트랙에서 겹침을 생성하는 변경이 발생했는지 감지합니다.
 *
 * 겹침 생성 조건:
 * 1. 아이템 duration 증가 (동일 아이템의 durationInFrames 증가)
 * 2. 아이템 추가 (트랙의 아이템 수 증가)
 */
const hasOverlapCreatingChange = (
  prevState: EditorState,
  newState: EditorState,
  track: TrackType,
): boolean => {
  const prevItems = prevState.undoableState.items;
  const newItems = newState.undoableState.items;

  // 트랙의 아이템들을 확인 - duration 증가 감지
  for (const itemId of track.items) {
    const prevItem = prevItems[itemId];
    const newItem = newItems[itemId];

    if (prevItem && newItem) {
      if (newItem.durationInFrames > prevItem.durationInFrames) {
        return true;
      }
    }
  }

  // 이전 상태의 트랙에서 아이템이 추가되었는지 확인
  const prevTrack = prevState.undoableState.tracks.find(
    (t) => t.id === track.id,
  );
  if (prevTrack) {
    // 새로 추가된 아이템이 있는지 확인
    for (const itemId of track.items) {
      if (!prevTrack.items.includes(itemId)) {
        // 아이템이 새로 추가됨
        return true;
      }
    }
  }

  return false;
};

/**
 * 특정 트랙의 아이템들을 재배치하여 겹침을 제거합니다.
 * 새로 추가된 아이템 블록을 우선 배치하고, 겹치는 기존 아이템들을 뒤로 밉니다.
 *
 * 예시:
 * - 기존: [A: 0-30] [B: 30-60] [C: 60-90]
 * - 새 아이템: [X: 30-45] [Y: 45-60] [Z: 60-75]
 * - 결과: [A: 0-30] [X: 30-45] [Y: 45-60] [Z: 60-75] [B: 75-105] [C: 105-135]
 *
 * @param prevState - 변경 전 상태 (새 아이템 판별용)
 */
const resolveOverlapsForTrack = (
  state: EditorState,
  track: TrackType,
  prevState: EditorState,
): EditorState => {
  if (track.items.length === 0) {
    return state;
  }

  // 이전 상태의 트랙에서 기존 아이템 ID 목록 가져오기
  const prevTrack = prevState.undoableState.tracks.find(
    (t) => t.id === track.id,
  );
  const prevItemIds = new Set(prevTrack?.items ?? []);

  // 모든 아이템 가져오기
  const allItems = track.items
    .map((id) => state.undoableState.items[id])
    .filter((item): item is EditorStarterItem => item !== undefined);

  if (allItems.length === 0) {
    return state;
  }

  // 새 아이템과 기존 아이템 분리
  const newItems = allItems.filter((item) => !prevItemIds.has(item.id));
  const existingItems = allItems.filter((item) => prevItemIds.has(item.id));

  // 새 아이템이 없으면 기존 간격 제거 로직으로 처리
  if (newItems.length === 0) {
    return collapseGapsForTrack(state, track);
  }

  // 새 아이템들의 시작 위치 (최소 from)
  const newItemsMinFrom = Math.min(...newItems.map((item) => item.from));

  // 기존 아이템을 두 그룹으로 분리:
  // - beforeItems: 새 아이템 블록 시작 전에 완전히 끝나는 아이템들 (유지)
  // - afterItems: 새 아이템 블록과 겹치거나 이후에 시작하는 아이템들 (뒤로 밀기)
  const beforeItems = existingItems.filter(
    (item) => item.from + item.durationInFrames <= newItemsMinFrom,
  );
  const afterItems = existingItems.filter(
    (item) => item.from + item.durationInFrames > newItemsMinFrom,
  );

  // 각 그룹 정렬
  beforeItems.sort((a, b) => a.from - b.from);
  newItems.sort((a, b) => a.from - b.from);
  afterItems.sort((a, b) => a.from - b.from);

  // 최종 순서: before → new → after
  const orderedItems = [...beforeItems, ...newItems, ...afterItems];

  // 첫 번째 아이템의 위치는 유지하고, 나머지는 순차적으로 배치
  let nextStartFrame = orderedItems[0]?.from ?? 0;
  const updatedItems: Record<string, EditorStarterItem> = {};
  const itemShiftFrames = new Map<string, number>();
  let hasChanges = false;

  for (const item of orderedItems) {
    if (item.from !== nextStartFrame) {
      const shiftFrames = nextStartFrame - item.from;
      updatedItems[item.id] = {
        ...item,
        from: nextStartFrame,
      };
      itemShiftFrames.set(item.id, shiftFrames);
      hasChanges = true;
    }
    nextStartFrame += item.durationInFrames;
  }

  if (!hasChanges) {
    return state;
  }

  const resolvedState = {
    ...state,
    undoableState: {
      ...state.undoableState,
      items: {
        ...state.undoableState.items,
        ...updatedItems,
      },
    },
  };

  return applyLinkedTrackShifts({
    state: resolvedState,
    sourceTrack: track,
    itemShiftFrames,
  });
};

/**
 * State 변경 후 magnetic track의 간격과 겹침을 자동으로 처리하는 post-processor입니다.
 *
 * setState 호출 시 자동으로 적용되어 다음 케이스들을 처리합니다:
 * - 아이템 삭제 → 간격 제거 (collapse)
 * - 아이템 축소 → 간격 제거 (collapse)
 * - 아이템 확장 → 겹침 제거 (push)
 * - 아이템 추가 → 겹침 제거 (push)
 *
 * ※ 드래그 중(itemsBeingTrimmed)에는 실행하지 않음 - UX 고려
 * ※ 드래그가 끝나면 (itemsBeingTrimmed가 해제되면) 강제로 간격/겹침 체크
 *
 * @param prevState - 변경 전 상태
 * @param newState - 변경 후 상태
 * @returns 간격/겹침이 처리된 새로운 상태
 */
export const autoCollapseMagneticGaps = (
  prevState: EditorState,
  newState: EditorState,
): EditorState => {
  // 트리밍/드래그 중이면 스킵 (UX: 실시간으로 처리하면 어색함)
  if (newState.timelineTimeEditApplied) {
    const { timelineTimeEditApplied: _handled, ...handledState } = newState;
    return handledState;
  }
  if (newState.itemsBeingTrimmed.length > 0) {
    return newState;
  }

  // Trim이 방금 끝났는지 확인 (prev에는 있었는데 new에는 없음)
  const trimJustFinished =
    prevState.itemsBeingTrimmed.length > 0 &&
    newState.itemsBeingTrimmed.length === 0;

  // undoableState가 변경되지 않았고, trim도 끝나지 않았으면 스킵
  if (prevState.undoableState === newState.undoableState && !trimJustFinished) {
    return newState;
  }

  let result = newState;
  let hasAnyChange = false;

  for (const track of newState.undoableState.tracks) {
    // Magnetic이 비활성화된 트랙은 스킵
    if (!track.magnetic) {
      continue;
    }

    // 1. 간격 처리 (Collapse)
    const shouldCollapseGaps = trimJustFinished
      ? hasGapsInTrack(result, track)
      : hasGapCreatingChange(prevState, newState, track);

    if (shouldCollapseGaps) {
      const collapsed = collapseGapsForTrack(result, track);
      if (collapsed !== result) {
        result = collapsed;
        hasAnyChange = true;
      }
    }

    // 2. 겹침 처리 (Push) - 최신 result 기준으로 트랙 다시 가져오기
    const currentTrack = result.undoableState.tracks.find(
      (t) => t.id === track.id,
    );
    if (!currentTrack) continue;

    const shouldResolveOverlaps = trimJustFinished
      ? hasOverlapsInTrack(result, currentTrack)
      : hasOverlapCreatingChange(prevState, newState, track);

    if (shouldResolveOverlaps) {
      const resolved = resolveOverlapsForTrack(result, currentTrack, prevState);
      if (resolved !== result) {
        result = resolved;
        hasAnyChange = true;
      }
    }
  }

  if (hasAnyChange) {
    console.log(
      '[autoCollapseMagneticGaps] Gaps/overlaps resolved automatically',
    );
  }

  return syncCaptionsToChangedVideoRanges(prevState, result);
};
